import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

const Header = ({ setMobileMenuOpen }) => {
  const { pathname } = useLocation();
  const [menuOpen, setIsMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    setMobileMenuOpen(menuOpen);
  }, [menuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setAtTop(currentY < 10);

      if (currentY > lastScrollY.current && currentY > 80) {
        // Scrolling DOWN — hide navbar
        setHidden(true);
      } else {
        // Scrolling UP — show navbar
        setHidden(false);
      }
      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuitems = [
    { name: "Home", url: "/" },
    { name: "Our Story", url: "/ourstory" },
    { name: "Our App", url: "/ourapp" },
    { name: "Events", url: "/events" },
    { name: "Become an Ally", url: "/becomeanally" },
    { name: "Newsletter", url: "/resources" },
    { name: "Blog", url: "/blog" },
  ];

  const handleDownload = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent) || /Windows/i.test(userAgent)) {
      window.plausible("Play Store Click");
      window.location.href =
        "https://play.google.com/store/apps/details?id=com.social_spectrum.mobile";
    } else if (/iPad|iPhone|iPod|ios/.test(userAgent) && !window.MSStream) {
      window.plausible("App Store Click");
      window.location.href =
        "https://apps.apple.com/us/app/social-spectrum/id6473112455";
    } else {
      window.plausible("Play Store Click");
      window.open(
        "https://play.google.com/store/apps/details?id=com.social_spectrum.mobile",
        "_blank"
      );
    }
  };

  const handleclick = () => setIsMenuOpen(!menuOpen);
  const closemenu = () => setIsMenuOpen(false);
  const mobref = useRef();

  useEffect(() => {
    const closeMenuHandler = (e) => {
      if (
        mobref.current &&
        !mobref.current.contains(e.target) &&
        !e.target.closest(".menubarbtn")
      ) {
        setIsMenuOpen(false);
      }
    };
    document.body.addEventListener("click", closeMenuHandler);
    return () => document.body.removeEventListener("click", closeMenuHandler);
  });

  return (
    <div
      className="w-full flex justify-center bg-white sticky top-0 z-50"
      style={{
        transform: hidden ? "translateY(-100%)" : "translateY(0)",
        transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, background 0.3s ease",
        boxShadow: atTop ? "none" : "0px 2px 20px rgba(0, 0, 0, 0.08)",
        borderBottom: "none",
      }}
    >
      <div className="w-full max-w-[1280px] px-6 md:px-16 h-[68px] md:h-[78px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <img
            className="h-[32px] sm:h-[36px] md:h-[40px] w-auto"
            src={process.env.PUBLIC_URL + "/images/sslogo.svg"}
            alt="Social Spectrum logo"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {menuitems.map((item, i) => (
            <Link to={item.url} key={i}>
              <span
                className="text-[15px] transition-all duration-200 relative group font-nunito"
                style={{
                  color: pathname === item.url ? "#1167D0" : "#4B4E56",
                  fontWeight: pathname === item.url ? 600 : 400,
                }}
              >
                {item.name}
                <span
                  className="absolute bottom-[-3px] left-0 h-[2px] rounded-full transition-all duration-300"
                  style={{
                    background: "#1167D0",
                    width: pathname === item.url ? "100%" : "0%",
                  }}
                />
              </span>
            </Link>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={handleDownload}
            className="py-[10px] px-[22px] text-[14px] font-[600] rounded-[32px] font-nunito transition-all duration-200 text-[#1167D0] bg-white border-[1.5px] border-[#1167D0] hover:bg-[#F0F6FF] hover:scale-105"
          >
            Download Now
          </button>
          <a
            href="https://app.socialspectrum.org/"
            className="py-[10px] px-[22px] text-white text-[14px] font-[600] rounded-[32px] font-nunito transition-all duration-200 bg-[#1167D0] shadow-[0_4px_14px_rgba(17,103,208,0.35)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(17,103,208,0.45)]"
          >
            Login / Sign up
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="flex lg:hidden flex-col gap-1.5 p-2 menubarbtn"
          onClick={handleclick}
          aria-label="Open menu"
        >
          <span
            className={`block w-6 h-[2px] bg-[#0D121F] rounded transition-all duration-300 origin-center ${menuOpen ? "rotate-45 translate-y-[7px]" : ""
              }`}
          />
          <span
            className={`block w-6 h-[2px] bg-[#0D121F] rounded transition-all duration-300 ${menuOpen ? "opacity-0 scale-x-0" : ""
              }`}
          />
          <span
            className={`block w-6 h-[2px] bg-[#0D121F] rounded transition-all duration-300 origin-center ${menuOpen ? "-rotate-45 -translate-y-[7px]" : ""
              }`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          ref={mobref}
          className="lg:hidden absolute top-[68px] left-0 w-full bg-white flex flex-col py-4"
          style={{ boxShadow: "0px 8px 24px rgba(0,0,0,0.08)" }}
        >
          {menuitems.map((item, i) => (
            <Link
              to={item.url}
              onClick={closemenu}
              key={i}
              className="px-6 py-3 text-[15px] border-b border-[#F3F4F6] last:border-b-0 font-nunito transition-colors"
              style={{
                color: pathname === item.url ? "#1167D0" : "#4B4E56",
                fontWeight: pathname === item.url ? 600 : 400,
              }}
            >
              {item.name}
            </Link>
          ))}
          <div className="px-6 pt-4 pb-2 flex flex-col gap-3">
            <button
              onClick={() => { handleDownload(); closemenu(); }}
              className="w-full py-3 px-6 text-[15px] font-[600] rounded-[32px] font-nunito transition-all duration-200 text-[#1167D0] bg-white border-[1.5px] border-[#1167D0]"
            >
              Download Now
            </button>
            <a
              href="https://app.socialspectrum.org/"
              onClick={closemenu}
              className="w-full py-3 px-6 text-center text-white text-[15px] font-[600] rounded-[32px] font-nunito transition-all duration-200 bg-[#1167D0]"
            >
              Login / Sign up
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
